import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState, type UIEvent } from "react";
import { fetchConversation, fetchMessageMediaSignedUrl, postConversationReply, type AuditEvent, type ConversationMessage, type ConversationSummary, type SignedMediaInfo } from "../lib/api";
import { getUserInfo } from "../lib/auth";
import { useDocumentTitle } from "../hooks/use-document-title";
import { useConversationSocket } from "../hooks/use-conversation-socket";
import { MediaLightbox } from "../components/MediaLightbox";

interface DisplayMessage extends ConversationMessage {
  pending?: boolean;
}

/**
 * Inbound media (images etc.) is downloaded server-side once on receipt and stored in our own S3
 * bucket - never the raw Twilio URL, which needs Twilio's own credentials to fetch and so can
 * never be hotlinked from a browser. Each render fetches a fresh short-lived (15 min) CloudFront
 * signed URL for this one object rather than caching it, since a long-open page could otherwise
 * hold an expired URL.
 */
function InboundMediaImage({ messageId, index }: { messageId: string; index: number }) {
  const [media, setMedia] = useState<SignedMediaInfo | null>(null);
  const [failed, setFailed] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMedia(null);
    setFailed(false);
    fetchMessageMediaSignedUrl(messageId, index)
      .then((info) => {
        if (!cancelled) setMedia(info);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [messageId, index]);

  if (failed) return <div className="message-media-error">Attachment unavailable</div>;
  if (!media) return <div className="message-media-loading">Loading attachment&hellip;</div>;

  if (!media.contentType.startsWith("image/")) {
    return (
      <a href={media.signedUrl} target="_blank" rel="noopener noreferrer" className="message-media-fallback">
        Attachment ({media.contentType})
      </a>
    );
  }

  return (
    <>
      <img
        src={media.signedUrl}
        alt="Attachment from dealer"
        className="message-media-image"
        onClick={() => setLightboxOpen(true)}
      />
      {lightboxOpen ? (
        <MediaLightbox src={media.signedUrl} alt="Attachment from dealer" onClose={() => setLightboxOpen(false)} />
      ) : null}
    </>
  );
}

type TimelineItem =
  | { kind: "message"; data: DisplayMessage }
  | { kind: "audit"; data: AuditEvent };

function formatAuditAction(action: string): string {
  return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatAuditAuthority(authority: string): string {
  switch (authority) {
    case "user_interaction": return "User interaction";
    case "system_process": return "System process";
    case "ai_determination": return "AI determination";
    default: return authority;
  }
}

function formatAuditDiff(before: Record<string, unknown> | null, after: Record<string, unknown> | null): string {
  if (!before && !after) return "";
  const parts: string[] = [];
  const allKeys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  for (const key of allKeys) {
    const bVal = before?.[key];
    const aVal = after?.[key];
    const bStr = bVal != null ? JSON.stringify(bVal) : "—";
    const aStr = aVal != null ? JSON.stringify(aVal) : "—";
    if (bStr !== aStr) {
      parts.push(`${key}: ${bStr} → ${aStr}`);
    }
  }
  return parts.join("\n");
}

function formatChannel(channel: string): string {
  if (channel === "whatsapp") return "WhatsApp";
  return channel;
}

function titleCase(str: string): string {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ConversationDetailPage() {
  const { dealerId } = useParams<{ dealerId: string }>();
  const [conversation, setConversation] = useState<ConversationSummary | null>(null);
  useDocumentTitle(
    conversation?.dealerName
      ? `Conversation (${titleCase(conversation.dealerName)})`
      : dealerId
        ? `Conversation (Dealer #${dealerId})`
        : "Conversation",
  );
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [canSendFreeform, setCanSendFreeform] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestInboundAt = useRef<number | null>(null);

  const handleSocketMessage = useCallback((incoming: ConversationMessage) => {
    if (incoming.direction === "inbound") {
      const inboundMs = Date.parse(incoming.timestamp);
      if (Number.isFinite(inboundMs)) {
        latestInboundAt.current = inboundMs;
      }
      setSessionExpired(false);
      setCanSendFreeform(true);
    }
    setMessages((prev) => {
      if (prev.some((m) => !m.pending && m.messageId === incoming.messageId)) {
        return prev;
      }
      const withoutPending = prev.filter((m) => {
        if (!m.pending) return true;
        return m.content !== incoming.content || m.direction !== incoming.direction;
      });
      return [incoming, ...withoutPending];
    });
  }, []);

  const { status: wsStatus } = useConversationSocket({ dealerId, onMessage: handleSocketMessage });

  const loadConversation = useCallback(
    async (cursor?: string) => {
      if (!dealerId) {
        throw new Error("missing_dealer_id");
      }
      const res = await fetchConversation(dealerId, { cursor, limit: 50 });
      return res;
    },
    [dealerId],
  );

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!dealerId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await loadConversation();
        if (!mounted) return;
        setConversation(res.conversation);
        setMessages(res.messages);
        setNextCursor(res.nextCursor);
        setCanSendFreeform(res.canSendFreeform);
        setSessionExpired(res.sessionExpired);
        setAuditEvents(res.auditEvents ?? []);
        if (res.latestInboundAt) {
          latestInboundAt.current = Date.parse(res.latestInboundAt);
        }
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : "Failed to load conversation");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [dealerId, loadConversation]);

  useEffect(() => {
    const SESSION_WINDOW_MS = 24 * 60 * 60 * 1000;
    const interval = setInterval(() => {
      const ts = latestInboundAt.current;
      if (ts && Number.isFinite(ts)) {
        const expired = Date.now() - ts > SESSION_WINDOW_MS;
        setSessionExpired(expired);
        setCanSendFreeform(!expired);
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  const loadOlderMessages = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await loadConversation(nextCursor);
      setNextCursor(res.nextCursor);
      setMessages((prev) => {
        const existing = new Set(prev.map((m) => m.messageId));
        const older = res.messages.filter((m) => !existing.has(m.messageId));
        return [...prev, ...older];
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load older messages");
    } finally {
      setLoadingMore(false);
    }
  }, [loadConversation, loadingMore, nextCursor]);

  const onScrollMessages = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
      if (atBottom) {
        void loadOlderMessages();
      }
    },
    [loadOlderMessages],
  );

  const sendReply = useCallback(async () => {
    if (!dealerId) return;
    const content = reply.trim();
    if (!content || sendingReply) return;

    const pendingId = `pending-${Date.now()}`;
    const pendingMsg: DisplayMessage = {
      messageId: pendingId,
      conversationId: conversation?.conversationId ?? "",
      direction: "outbound",
      content,
      messageType: "text",
      timestamp: new Date().toISOString(),
      metadata: { source: "dashboard", sentBy: "You" },
      pending: true,
    };

    setMessages((prev) => [pendingMsg, ...prev]);
    setReply("");
    setSendingReply(true);
    setError(null);

    try {
      const user = getUserInfo();
      const senderName = user?.givenName || undefined;
      const result = await postConversationReply({ dealerId, content, senderName });
      if (!result.success) {
        setMessages((prev) => prev.filter((m) => m.messageId !== pendingId));
        setError(result.message ?? "Failed to send message");
      } else {
        setTimeout(() => {
          setMessages((prev) =>
            prev.map((m) => (m.messageId === pendingId ? { ...m, pending: false } : m)),
          );
        }, 8000);
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.messageId !== pendingId));
      setError(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      setSendingReply(false);
    }
  }, [conversation, dealerId, reply, sendingReply]);

  if (loading) return <div className="loading">Loading conversation...</div>;
  if (error) return <div className="error-msg">{error}</div>;
  if (!conversation) return <div className="empty-state">No conversation found for this dealer</div>;

  const conv = conversation;

  return (
    <div className="conversation-container">
      <div className="card-header">
        <h1 className="page-title">
          Conversation &mdash;{" "}
          <Link to={`/dealer/${dealerId}`} className="dealer-link">
            {conv.dealerName
              ? `${titleCase(conv.dealerName)} (${titleCase(conv.dealerOrg ?? String(dealerId))})`
              : `Dealer #${dealerId}`}
          </Link>
        </h1>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div>
          <strong>Channel:</strong> {formatChannel(conv.channel)} &nbsp;
          <strong>State:</strong> {conv.state} &nbsp;
          <span className={`badge ${wsStatus === "connected" ? "badge-success" : wsStatus === "connecting" ? "badge-info" : "badge-error"}`}>
            {wsStatus === "connected" ? "Live" : wsStatus === "connecting" ? "Connecting..." : "Disconnected"}
          </span>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="form-group" style={{ marginBottom: 10 }}>
          <label htmlFor="conversation-reply">Reply to dealer</label>
          <input
            id="conversation-reply"
            type="text"
            value={reply}
            onChange={(event) => setReply(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") void sendReply(); }}
            placeholder={sessionExpired ? "Type your message..." : "Write a reply..."}
            disabled={sendingReply}
          />
        </div>
        {reply.trim().length > 0 && (
          <div className="template-preview">
            <div className="template-preview-label">Dealer will receive:</div>
            <div className="template-preview-body">
              {sessionExpired && <>Message from TradeBid:{"\n"}</>}
              {reply.trim()}{"\n\n"}
              Sent by {getUserInfo()?.givenName || "Otto"}. To modify your WhatsApp communication preferences simply say &lsquo;Update my preferences&rsquo;.
            </div>
          </div>
        )}
        <div className="conversation-reply-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              void sendReply();
            }}
            disabled={sendingReply || reply.trim().length === 0}
          >
            {sendingReply ? "Sending..." : sessionExpired ? "Send via template" : "Send"}
          </button>
          {sessionExpired ? (
            <span className="badge badge-warning" style={{ marginLeft: 8 }}>
              Session expired &mdash; will send via template
            </span>
          ) : (
            <span className="badge badge-success" style={{ marginLeft: 8 }}>
              Session active &mdash; freeform
            </span>
          )}
        </div>
      </div>

      <div className="message-list conversation-message-list" onScroll={onScrollMessages}>
        {messages.length === 0 && auditEvents.length === 0 && (
          <div className="empty-state">No messages in this conversation</div>
        )}
        {(() => {
          const timeline: TimelineItem[] = [
            ...messages.map((m): TimelineItem => ({ kind: "message", data: m })),
            ...auditEvents.map((a): TimelineItem => ({ kind: "audit", data: a })),
          ];
          timeline.sort((a, b) => {
            const ta = a.kind === "message" ? a.data.timestamp : a.data.timestamp;
            const tb = b.kind === "message" ? b.data.timestamp : b.data.timestamp;
            return tb.localeCompare(ta);
          });

          return timeline.map((item) => {
            if (item.kind === "audit") {
              const audit = item.data;
              const diff = formatAuditDiff(audit.before, audit.after);
              return (
                <div key={audit.auditId} className="audit-event">
                  <div className="audit-event-icon">⚡</div>
                  <div className="audit-event-content">
                    <div className="audit-event-action">
                      {formatAuditAction(audit.action)}
                      <span className="badge badge-accent" style={{ marginLeft: 6 }}>
                        {formatAuditAuthority(audit.authority)}
                      </span>
                    </div>
                    {diff && <div className="audit-event-diff">{diff}</div>}
                    {typeof audit.metadata?.note === "string" && (
                      <div className="audit-event-note" style={{ fontStyle: "italic", color: "var(--color-text-secondary)" }}>
                        {audit.metadata.note}
                      </div>
                    )}
                    <div className="audit-event-meta">
                      {audit.actor} &middot;{" "}
                      <span className="mono">{new Date(audit.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              );
            }

            const msg = item.data;
            const mediaKeys = (msg.metadata?.mediaKeys as (string | null)[] | undefined) ?? [];
            const isTemplate = msg.messageType === "template";
            const isDashboard = msg.metadata?.source === "dashboard" && msg.direction === "outbound";
            const hasSenderMeta = typeof msg.metadata?.senderFirstName === "string";
            const showFooter = isDashboard && (isTemplate || hasSenderMeta);
            const senderName = typeof msg.metadata?.senderFirstName === "string"
              ? msg.metadata.senderFirstName
              : typeof msg.metadata?.sentBy === "string"
                ? (msg.metadata.sentBy as string).split(" ")[0]
                : "Otto";

            return (
              <div
                key={msg.messageId}
                className={`message-bubble ${msg.direction}${msg.pending ? " pending" : ""}${isTemplate ? " template" : ""}`}
              >
                {showFooter ? (
                  <div className="template-message-body">
                    {isTemplate && <div className="template-message-header">Message from TradeBid:</div>}
                    <div>{msg.content}</div>
                    <div className="template-message-footer">
                      Sent by {senderName}. To modify your WhatsApp communication preferences simply say &lsquo;Update my preferences&rsquo;.
                    </div>
                  </div>
                ) : (
                  <div>{msg.content}</div>
                )}
                {mediaKeys.map((key, index) =>
                  key ? <InboundMediaImage key={index} messageId={msg.messageId} index={index} /> : null,
                )}
                <div className="message-meta">
                  {msg.direction === "inbound"
                    ? "Dealer"
                    : typeof msg.metadata?.sentBy === "string"
                      ? `Sent by: ${msg.metadata.sentBy}`
                      : "Otto"}{" "}
                  &middot;{" "}
                  <span className="mono">{new Date(msg.timestamp).toLocaleString()}</span>
                  {msg.pending && (
                    <span className="badge badge-neutral" style={{ marginLeft: 6 }}>
                      Sending...
                    </span>
                  )}
                  {isTemplate && !msg.pending && (
                    <span className="badge badge-warning" style={{ marginLeft: 6 }}>
                      template
                    </span>
                  )}
                </div>
              </div>
            );
          });
        })()}
        {loadingMore && <div className="loading">Loading older messages...</div>}
      </div>
    </div>
  );
}
